import { Component, lazy, type ReactNode, Suspense } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { ErrorState, LoadingState } from '../State';
import s from './Map.module.css';

export interface MapMarker {
  id: string;
  lng: number;
  lat: number;
  label?: string;
  tone?: 'primary' | 'good' | 'medium' | 'bad' | 'neutral';
  selected?: boolean;
}

/** A polyline (a track, a route). Coordinates are [lng, lat]. */
export interface MapLine {
  id: string;
  coordinates: [number, number][];
  tone?: 'primary' | 'good' | 'medium' | 'bad' | 'neutral' | 'accent';
  /** Drawn thicker and on top (e.g. the selected part of a track). */
  emphasis?: boolean;
}

export interface MapProps {
  /** Accessible description of what the map shows. */
  label: string;
  center?: [number, number];
  zoom?: number;
  markers?: MapMarker[];
  /** Fit the view to the markers (and lines) whenever they change. */
  fitToMarkers?: boolean;
  onMarkerClick?: (id: string) => void;
  /** Lines drawn under the markers. */
  lines?: MapLine[];
  /** Pan to this [lng, lat] whenever it changes (e.g. the event picked in a list). */
  focus?: [number, number] | null;
  height?: 'sm' | 'md' | 'lg' | 'fill';
  className?: string;
}

/** Window event other features (the assistant's show_on_map) use to highlight things. */
export const SHOW_ON_MAP_EVENT = 'nova:show-on-map';
export interface ShowOnMapDetail {
  vehicle_ids: string[];
  place_ids: string[];
}

const MapInner = lazy(() => import('./MapInner'));

/**
 * A map that cannot start (no WebGL, a broken style) must not take the page down with it:
 * the rest of the page stays usable and the map says why it is missing.
 */
class MapBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  override state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  override render() {
    if (this.state.error) {
      return <ErrorState compact error={this.state.error} className={s.loading} onRetry={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}

/**
 * The only map (RULES §UI-2, §UI-9): MapLibre GL, loaded on demand, with the style from
 * `/api/me.map` (light or dark to match the theme) and the attribution control always on.
 */
export function Map({ height = 'md', className, ...props }: MapProps) {
  // translate="no": browser auto-translate rewrites map DOM and breaks it (parity inventory 2 §0).
  return (
    <div className={cx(s.frame, s[height], className)} translate="no">
      <MapBoundary>
        <Suspense fallback={<LoadingState label={t('Loading map…')} className={s.loading} />}>
          <MapInner {...props} />
        </Suspense>
      </MapBoundary>
    </div>
  );
}
